import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DesktopLayout from '../../components/layout/DesktopLayout';
import api from '../../services/api';
import type { ApiAd } from '../../types';
import './AdDetailPage.css';

/**
 * 廣告詳情頁面
 * 對應廣告詳情 UX 流程
 */
const AdDetailPage = () => {
  const { adId } = useParams<{ adId: string }>();
  const navigate = useNavigate();
  const [ad, setAd] = useState<ApiAd | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Redirect to list page with query string on desktop
  useEffect(() => {
    if (window.innerWidth >= 768 && adId) {
      navigate(`/ads?id=${adId}`);
      return;
    }
  }, [adId, navigate]);

  useEffect(() => {
    const loadAd = async () => {
      console.log('[AdDetailPage] useEffect triggered, adId:', adId);

      if (!adId) {
        setLoading(false);
        setError('刊登 ID 不存在');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch ad from API
        console.log('[AdDetailPage] Fetching ad from API:', `/api/ads/${adId}`);
        const response = await api.get<ApiAd>(`/api/ads/${adId}`);

        console.log('[AdDetailPage] API response:', response);
        if (response.success && response.data) {
          console.log('[AdDetailPage] Setting ad data:', response.data);
          setAd(response.data);
        } else {
          console.error('[AdDetailPage] API response unsuccessful or no data');
          setError('找不到此刊登');
        }
      } catch (err) {
        console.error('[AdDetailPage] Error loading ad:', err);
        setError('載入刊登時發生錯誤');
      } finally {
        console.log('[AdDetailPage] Setting loading to false');
        setLoading(false);
      }
    };

    loadAd();
  }, [adId]);

  const handleCall = () => {
    if (ad?.phone) {
      window.location.href = `tel:${ad.phone}`;
    }
  };

  const handleBack = () => {
    navigate('/ads');
  };

  if (loading) {
    return (
      <div className="ad-detail-page">
        <DesktopLayout>
        <div className="ad-detail-page__loading">載入中...</div>
        </DesktopLayout>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="ad-detail-page">
        <DesktopLayout>
        <div className="ad-detail-page__container">
          <div className="ad-detail-page__not-found">
            <h1>找不到此刊登</h1>
            <p>{error || '此刊登可能已下架或不存在。'}</p>
            <button onClick={handleBack} className="ad-detail-page__back-link">
              返回刊登列表
            </button>
          </div>
        </div>
        </DesktopLayout>
      </div>
    );
  }

  const images = ad.imageUrls && ad.imageUrls.length > 0 ? ad.imageUrls : [];

  return (
    <div className="ad-detail-page">
      <DesktopLayout>

      <main className="ad-detail-page__container">
        <nav className="ad-detail-page__breadcrumb">
          <button onClick={() => navigate('/')} style={{background: 'none', border: 'none', color: '#0066cc', cursor: 'pointer', textDecoration: 'underline'}}>首頁</button>
          {' > '}
          <button onClick={() => navigate('/ads')} style={{background: 'none', border: 'none', color: '#0066cc', cursor: 'pointer', textDecoration: 'underline'}}>刊登列表</button>
          {' > '}
          <span>{ad.title}</span>
        </nav>

        <div className="ad-detail-page__content">
          {/* 左側：圖片 */}
          <div className="ad-detail-page__media">
            {images.length > 0 && (
              <>
                <div className="ad-detail-page__main-image">
                  <img src={images[activeImageIndex]} alt={ad.title} />
                </div>
                {images.length > 1 && (
                  <div className="ad-detail-page__thumbnails">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        className={`ad-detail-page__thumbnail ${
                          index === activeImageIndex ? 'active' : ''
                        }`}
                        onClick={() => setActiveImageIndex(index)}
                      >
                        <img src={img} alt={`${ad.title} - 圖片 ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* 右側：資訊 */}
          <div className="ad-detail-page__info">
            <h1 className="ad-detail-page__title">{ad.title}</h1>

            <div className="ad-detail-page__company">
              <strong>{ad.storeName}</strong>
            </div>

            <div className="ad-detail-page__tags">
              <span className="tag tag--region">
                📍 {ad.region}
              </span>
              {ad.district && (
                <span className="tag tag--region">
                  📍 {ad.district}
                </span>
              )}
              <span className="tag tag--service">
                🔧 {ad.category}
              </span>
              {ad.tags.map((tag) => (
                <span key={tag} className="tag tag--service">
                  🔧 {tag}
                </span>
              ))}
            </div>

            {ad.description && (
              <div className="ad-detail-page__section">
                <h2>服務描述</h2>
                <p>{ad.description}</p>
              </div>
            )}

            {ad.address && (
              <div className="ad-detail-page__section">
                <h2>地址</h2>
                <p>{ad.address}</p>
              </div>
            )}

            {ad.priceRange && (
              <div className="ad-detail-page__section">
                <h2>價格範圍</h2>
                <p>{ad.priceRange}</p>
              </div>
            )}

            <div className="ad-detail-page__section">
              <h2>聯絡資訊</h2>
              <div className="ad-detail-page__contact">
                {ad.phone && (
                  <p>
                    <strong>電話：</strong> {ad.phone}
                  </p>
                )}
                {ad.address && (
                  <p>
                    <strong>地址：</strong> {ad.address}
                  </p>
                )}
              </div>
            </div>

            <div className="ad-detail-page__actions">
              {ad.phone && (
                <button className="btn btn--primary" onClick={handleCall}>
                  📞 立即撥打
                </button>
              )}
            </div>

            <div className="ad-detail-page__stats">
              <span>👁️ 瀏覽 {ad.viewCount} 次</span>
              <span>🖱️ 點擊 {ad.clickCount} 次</span>
            </div>
          </div>
        </div>
      </main>

      <footer className="ad-detail-page__footer">
        <p>&copy; 2026 瑋瑋車業. All rights reserved.</p>
      </footer>
      </DesktopLayout>
    </div>
  );
};

export default AdDetailPage;
